import { useEffect, useRef, useState, KeyboardEvent, FormEvent } from "react";
import { X, Send, Zap, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useOrganization } from "@/contexts/OrganizationContext";
import { usePulseUI, type PulseMessage } from "@/contexts/PulseContext";
import { usePulse, type PulseAction, type PulseConfidence, type PulseResponse } from "@/hooks/usePulse";

const ALLOWED_ROLES = new Set([
  "org_admin",
  "performance",
  "medical",
  "coach",
  "super_admin",
  "nutritionist",
  "physio",
  "analyst",
  "operations",
]);

const EXAMPLE_PROMPTS = [
  "Who's at risk for the next match?",
  "Show me today's wellness submissions",
  "What compliance items are outstanding?",
];

function ConfidenceBadge({ confidence }: { confidence: PulseConfidence }) {
  const map: Record<PulseConfidence, { label: string; cls: string }> = {
    high: { label: "High confidence", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/20" },
    medium: { label: "Medium confidence", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20" },
    low: { label: "Low confidence", cls: "bg-muted text-muted-foreground border-border" },
    no_data: { label: "No data", cls: "bg-muted text-muted-foreground border-border" },
  };
  const { label, cls } = map[confidence];
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium", cls)}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

function LoadingDots() {
  return (
    <div className="flex items-center gap-1 px-3 py-2" aria-label="Pulse is thinking">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-2 w-2 rounded-full bg-muted-foreground/60 animate-pulse"
          style={{ animationDelay: `${i * 150}ms`, animationDuration: "900ms" }}
        />
      ))}
    </div>
  );
}

function MessageBubble({ message, onAction }: { message: PulseMessage; onAction: (a: PulseAction) => void }) {
  if (message.role === "system") {
    return (
      <div className="flex justify-center my-2">
        <span className="text-xs text-muted-foreground italic px-3">{message.content}</span>
      </div>
    );
  }

  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-3.5 py-2 text-sm shadow-sm">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] rounded-2xl rounded-bl-sm bg-card border border-border px-3.5 py-2.5 text-sm shadow-sm space-y-2">
        <p className="whitespace-pre-wrap leading-relaxed text-foreground">{message.content}</p>
        {message.confidence && <ConfidenceBadge confidence={message.confidence} />}
        {message.answerType === "action" && message.actions && message.actions.length > 0 && (
          <div className="flex flex-col gap-1.5 pt-1">
            {message.actions.map((a) => (
              <Button
                key={a.actionId}
                size="sm"
                variant="default"
                className="justify-between"
                onClick={() => onAction(a)}
              >
                <span>{a.label}</span>
                <span aria-hidden>→</span>
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function PulsePanel() {
  const { isOpen, close, messages, addMessage, clearMessages, currentContext, setCurrentContext, pendingPrompt, setPendingPrompt } =
    usePulseUI();
  const { currentRole, isSuperAdmin } = useOrganization();
  const { sendMessage, isLoading } = usePulse();
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const allowed = isSuperAdmin || ALLOWED_ROLES.has(currentRole ?? "");

  // Auto-scroll on new message
  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current.querySelector("[data-radix-scroll-area-viewport]") as HTMLElement | null;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 200);
  }, [isOpen]);

  const submit = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !currentContext || isLoading) return;

    const snapshot = { entityName: currentContext.entityName, entityType: currentContext.entityType };
    addMessage({
      id: `u-${Date.now()}`,
      role: "user",
      content: trimmed,
      timestamp: new Date(),
      contextSnapshot: snapshot,
    });
    setInput("");

    const res: PulseResponse = await sendMessage(trimmed, currentContext);
    addMessage({
      id: `p-${Date.now()}`,
      role: "pulse",
      content: res.answer,
      answerType: res.answerType,
      confidence: res.confidence,
      actions: res.actions,
      followUpSuggestions: res.followUpSuggestions,
      contextSnapshot: snapshot,
      timestamp: new Date(),
    });
  };

  // Handle pending prompt from chips/external triggers
  useEffect(() => {
    if (pendingPrompt && isOpen) {
      const p = pendingPrompt;
      setPendingPrompt(null);
      submit(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt, isOpen]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(input);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  };

  const handleAction = async (a: PulseAction) => {
    // Re-submit as a confirmation message; backend action router handles execution
    await submit(a.label);
  };

  const clearEntityContext = () => {
    if (!currentContext) return;
    setCurrentContext({
      ...currentContext,
      entityType: null,
      entityId: null,
      entityName: null,
    });
  };

  const lastPulse = [...messages].reverse().find((m) => m.role === "pulse");
  const followUps = lastPulse?.followUpSuggestions ?? [];

  if (!allowed) return null;

  return (
    <>
      {/* Panel — slide from right on desktop, bottom on mobile */}
      <aside
        role="complementary"
        aria-label="Pulse assistant"
        aria-hidden={!isOpen}
        className={cn(
          "fixed z-50 bg-card border-border shadow-2xl flex flex-col",
          "transition-transform duration-[250ms] ease-out",
          // Mobile: bottom sheet
          "inset-x-0 bottom-0 top-16 border-t rounded-t-2xl",
          isOpen ? "translate-y-0" : "translate-y-full",
          // Desktop: right rail
          "md:inset-y-0 md:right-0 md:left-auto md:top-0 md:bottom-0 md:w-[420px] md:border-l md:border-t-0 md:rounded-none",
          isOpen ? "md:translate-x-0" : "md:translate-x-full md:translate-y-0",
        )}
      >
        {/* Header */}
        <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <Zap className="h-4 w-4 text-primary shrink-0" />
            <h2 className="text-base font-semibold tracking-tight">Pulse</h2>
            {currentContext?.entityName && (
              <Badge variant="secondary" className="gap-1 max-w-[180px]">
                <span className="truncate">{currentContext.entityName}</span>
                <button
                  onClick={clearEntityContext}
                  aria-label="Clear context"
                  className="hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (window.confirm("Clear conversation history?")) clearMessages();
                }}
                aria-label="Clear conversation"
                title="Clear conversation"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={close} aria-label="Close Pulse">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Body */}
        <ScrollArea ref={scrollRef} className="flex-1">
          <div className="px-4 py-4 space-y-3">
            {messages.length === 0 && !isLoading && (
              <div className="flex flex-col items-center justify-center text-center py-12 gap-4">
                <div className="rounded-full bg-primary/10 p-3">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground max-w-[260px]">
                  Ask me anything about your squad.
                </p>
                <div className="flex flex-col gap-2 w-full max-w-[280px]">
                  {EXAMPLE_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => submit(p)}
                      className="text-left text-sm px-3 py-2 rounded-lg border border-border bg-background hover:bg-accent transition-colors"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} onAction={handleAction} />
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm bg-card border border-border">
                  <LoadingDots />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Follow-ups */}
        {followUps.length > 0 && !isLoading && (
          <div className="px-4 pb-2 pt-1 border-t border-border/50">
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {followUps.map((s) => (
                <button
                  key={s}
                  onClick={() => submit(s)}
                  className="shrink-0 text-xs px-3 py-1.5 rounded-full border border-border bg-background hover:bg-accent transition-colors whitespace-nowrap"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="border-t border-border p-3 space-y-2 bg-card">
          {currentContext?.entityName && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground px-1">
              <span>Asking about {currentContext.entityName}</span>
              <button onClick={clearEntityContext} aria-label="Clear context" className="hover:text-foreground">
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about a player, session, load, or squad..."
              disabled={isLoading}
              className="flex-1"
            />
            <Button type="submit" size="icon" disabled={!input.trim() || isLoading} aria-label="Send">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </footer>
      </aside>
    </>
  );
}
